/**
 * The three pieces a list page needs to offer bulk actions: a row checkbox, a header
 * checkbox, and the bar that appears once something is selected.
 *
 * Paired with `hooks/useRowSelection`, which owns the state. These are presentation
 * only, so a page can adopt bulk actions without re-deciding what "some rows on this
 * page are ticked" should look like.
 *
 * **Tap targets.** A bare 16px checkbox is well under the 44px floor `.btn` already
 * enforces, and the e2e DOM audit flags it. Both boxes are wrapped in a 44px `<label>`
 * — which is also what `e2e/lib/checks/domChecks.ts` accepts as an interactive
 * ancestor, so the checkbox stops registering as a small target rather than being
 * excused from the check.
 */
import { ReactNode, useEffect, useRef } from 'react'

/** Hook for the e2e interaction sweep to find any page using this primitive. */
export const BULK_ROW_ATTR = 'data-bulk-row'

interface RowCheckboxProps {
  checked: boolean
  onChange: () => void
  /** What this row is — "WQAA100", "change #412". Becomes "Select WQAA100". */
  label: string
}

export function RowCheckbox({ checked, onChange, label }: RowCheckboxProps) {
  return (
    <label className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] cursor-pointer">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        checked={checked}
        onChange={onChange}
        aria-label={`Select ${label}`}
        {...{ [BULK_ROW_ATTR]: '' }}
      />
    </label>
  )
}

interface SelectAllCheckboxProps {
  state: 'none' | 'some' | 'all'
  onToggle: () => void
}

export function SelectAllCheckbox({ state, onToggle }: SelectAllCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null)

  // React has no prop for `indeterminate` — it is a DOM property, not an attribute,
  // so it has to be set imperatively or the header box reads as simply unchecked
  // when part of the page is selected.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'some'
  }, [state])

  return (
    <label className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] cursor-pointer">
      <input
        ref={ref}
        type="checkbox"
        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        checked={state === 'all'}
        onChange={onToggle}
        aria-label="Select all rows on this page"
      />
    </label>
  )
}

interface BulkActionBarProps {
  /** Ticked rows. */
  count: number
  /** Rows matching the current filters, across every page. */
  total: number
  pageSize: number
  allMatching: boolean
  onSelectAllMatching: () => void
  onClear: () => void
  /** The page's action buttons. */
  children: ReactNode
}

/**
 * Appears only when something is selected.
 *
 * **`sticky top-0`, at every width.** It needs to stay reachable while scrolling a
 * list that runs to thousands of rows — an action bar that scrolls away with the
 * header is one you have to scroll back up to find after choosing what to act on.
 *
 * It was `fixed bottom-0` under `sm` for exactly that reason, and that was wrong: a
 * viewport-pinned bar covers whatever is at the bottom of the screen *at rest*, which
 * on a phone is the row checkboxes — the one control that has to stay usable while
 * selecting. Tick one row and it could hide the ones you wanted next. The responsive
 * audit's occlusion check caught it on all three list pages, at both phone widths.
 *
 * Sticky solves both: in normal flow it covers nothing at rest, and it pins to the top
 * of the viewport once you scroll past it. Content passing *under* it mid-scroll is
 * what sticky is for, and the audit treats it that way.
 */
export function BulkActionBar({
  count,
  total,
  pageSize,
  allMatching,
  onSelectAllMatching,
  onClear,
  children,
}: BulkActionBarProps) {
  if (count === 0) return null

  // Only worth offering the escalation when there is genuinely more beyond this page.
  const canEscalate = !allMatching && count >= Math.min(pageSize, total) && total > count

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      aria-live="polite"
      className={
        'sticky top-0 z-20 mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 ' +
        'rounded-lg border border-primary-200 bg-primary-50 px-4 py-2 shadow-sm'
      }
    >
      <span className="text-sm font-medium text-primary-900">
        {allMatching
          ? `All ${total.toLocaleString()} matching rows selected.`
          : canEscalate
            ? `All ${count.toLocaleString()} on this page selected.`
            : `${count.toLocaleString()} selected`}
      </span>

      {canEscalate && (
        <button type="button" onClick={onSelectAllMatching} className="tap text-sm text-primary-700 underline">
          Select all {total.toLocaleString()} matching these filters
        </button>
      )}

      <span className="flex flex-wrap items-center gap-2 sm:ml-auto">
        {children}
        <button type="button" onClick={onClear} className="tap text-sm text-gray-600 underline">
          Clear
        </button>
      </span>
    </div>
  )
}
